import { createTheme, ThemeOptions } from '@mui/material/styles';

// Define theme colors based on the design system
// Corporate blue from Comnovo logo (#00558C)
const colors = {
  primary: {
    main: '#00558C', // Comnovo corporate blue
    light: '#0077B6',
    dark: '#003D66',
  },
  secondary: {
    main: '#9C27B0',
    light: '#BA68C8',
    dark: '#7B1FA2',
  },
  // The `dark` shades are the StatusChip text colour in light mode, on
  // alpha(main, 0.12) at 12px/600 — so they need 4.5:1, not 3:1. Ratios are
  // quoted for both surfaces because most chips sit in a table row, whose grey
  // tint costs ~0.35, and paper alone is too forgiving. Guarded by
  // theme/__tests__/statusChipContrast.test.ts.
  success: {
    main: '#10B981', // Modern green (Tailwind emerald-500)
    light: '#34D399',
    dark: '#047857', // emerald-700 — 4.92 paper / 4.58 row
  },
  warning: {
    main: '#F59E0B', // Modern amber (Tailwind amber-500)
    light: '#FCD34D',
    // amber-800, not -700: -700 cleared AA on paper (4.58) but failed in a row
    // (4.26), where `pending` / `needs_review` / `expiring_soon` are rendered.
    dark: '#92400E', // 6.47 paper / 6.03 row
  },
  error: {
    main: '#EF4444', // Modern red (Tailwind red-500)
    light: '#F87171',
    dark: '#B91C1C', // red-700 — 5.56 paper / 5.17 row
  },
  info: {
    main: '#3B82F6', // Modern blue (Tailwind blue-500)
    light: '#60A5FA',
    dark: '#2563EB',
  },
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// Use instead of `fontFamily: 'monospace'`, whose bare generic resolves to a
// different face on every OS. Leads with the JetBrains Mono webfont main.tsx
// already ships, so identifiers look the same on every machine.
// Keep in sync with `--font-mono` in index.css, which styles bare <code>.
//
// For machine identifiers only — IDs, MAC, IMEI, ICCID, secure serial/code,
// payloads, log output. Not status words, versions, OS names or dates.
export const fontFamilyMono =
  "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace";

// MUI sizes small controls at 0.8125rem (13px), which is not a step on the scale
// below. Chips are passive labels and sit at 12px; buttons are interactive and
// never the smallest text on screen. Shared because each theme sets its own
// `components`, so a wholesale MuiChip/MuiButton block would otherwise drop them.
const smallChipText = { fontSize: '0.75rem' };
const smallControlText = { fontSize: '0.875rem' };

// Define typography
const typography = {
  fontFamily: "'Inter Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  h1: {
    fontSize: '1.75rem', // 28px
    fontWeight: 700,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontSize: '1.5rem', // 24px
    fontWeight: 700,
    letterSpacing: '-0.01em',
  },
  h3: {
    fontSize: '1.25rem', // 20px
    fontWeight: 600,
  },
  // Page titles across the app are written as variant="h4"; size it like a page title.
  h4: {
    fontSize: '1.5rem', // 24px
    fontWeight: 700,
    letterSpacing: '-0.01em',
  },
  h5: {
    fontSize: '1rem', // 16px
    fontWeight: 600,
  },
  h6: {
    fontSize: '0.875rem', // 14px
    fontWeight: 600,
  },
  body1: {
    fontSize: '1rem', // 16px
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '0.875rem', // 14px
    lineHeight: 1.5,
  },
  caption: {
    fontSize: '0.75rem', // 12px
  },
};

// Create light theme
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
    grey: colors.grey,
    background: {
      default: colors.grey[50],
      paper: '#FFFFFF',
    },
    text: {
      primary: '#15202B',
      secondary: '#4A5A68',
    },
  },
  typography,
  spacing: 8, // 8px grid system
  shape: {
    borderRadius: 8,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 4,
          padding: '0.5rem 1rem',
        },
        sizeSmall: smallControlText,
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 4,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(21, 32, 43, 0.08)',
          border: '1px solid rgba(21, 32, 43, 0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
        },
        sizeSmall: smallChipText,
        filled: {
          boxShadow: 'none',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid',
          borderColor: 'transparent',
          // MUI 9 dropped the standard<Color> keys; match colorSeverity (color ?? severity),
          // as MUI's own Alert styles do, so severity-only alerts are covered.
          variants: [
            {
              props: { variant: 'standard', colorSeverity: 'success' },
              style: { borderColor: 'rgba(16, 185, 129, 0.2)' },
            },
            {
              props: { variant: 'standard', colorSeverity: 'warning' },
              style: { borderColor: 'rgba(245, 158, 11, 0.2)' },
            },
            {
              props: { variant: 'standard', colorSeverity: 'error' },
              style: { borderColor: 'rgba(239, 68, 68, 0.2)' },
            },
            {
              props: { variant: 'standard', colorSeverity: 'info' },
              style: { borderColor: 'rgba(59, 130, 246, 0.2)' },
            },
          ],
        },
      },
    },
  },
} as ThemeOptions);

// Create dark theme with improved readability
export const darkTheme = createTheme({
  ...lightTheme,
  palette: {
    mode: 'dark',
    // Lighter primary colors for better contrast in dark mode
    primary: {
      main: '#4A9FD8', // Lighter blue for dark mode
      light: '#6AB5E5',
      dark: '#2E7BB8',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#CE93D8', // Lighter purple for dark mode
      light: '#E1BEE7',
      dark: '#AB47BC',
      contrastText: '#000000',
    },
    // Brighter semantic colors for dark mode
    success: {
      main: '#66BB6A', // Brighter green (Material green-400)
      light: '#81C784',
      dark: '#4CAF50',
      contrastText: '#000000',
    },
    warning: {
      main: '#FFA726', // Brighter orange (Material orange-400)
      light: '#FFB74D',
      dark: '#FB8C00',
      contrastText: '#000000',
    },
    error: {
      main: '#EF5350', // Brighter red (Material red-400)
      // StatusChip text in dark mode: red-300 measured 3.63:1, red-200 is 5.04.
      light: '#EF9A9A',
      dark: '#F44336',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#42A5F5', // Brighter blue (Material blue-400)
      light: '#64B5F6',
      dark: '#2196F3',
      contrastText: '#000000',
    },
    grey: colors.grey,
    // Lighter dark backgrounds for better readability
    background: {
      default: '#1A1A1A', // Slightly lighter than grey[900] (#212121)
      paper: '#2D2D2D',   // Significantly lighter than grey[800] (#424242)
    },
    // High contrast text
    text: {
      primary: '#FFFFFF',      // Pure white for primary text
      secondary: '#B0B0B0',    // Light grey for secondary text (better than grey[300])
    },
    // Improved dividers and borders
    divider: 'rgba(255, 255, 255, 0.15)',
    action: {
      active: 'rgba(255, 255, 255, 0.7)',
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.16)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
  },
  components: {
    ...lightTheme.components,
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
          backgroundImage: 'none',
          backgroundColor: '#2D2D2D',
          border: '1px solid rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
          backgroundColor: '#2D2D2D',
          border: '1px solid rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#2D2D2D',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: '#FFFFFF',
            fontWeight: 600,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
        },
        sizeSmall: smallChipText,
        filled: {
          boxShadow: 'none',
        },
        outlined: {
          borderWidth: '1.5px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 4,
          padding: '0.5rem 1rem',
        },
        sizeSmall: smallControlText,
        contained: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 4,
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.23)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#4A9FD8',
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#B0B0B0',
          },
          '& .MuiInputBase-input': {
            color: '#FFFFFF',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid',
          // See the light theme's MuiAlert override for why this keys on colorSeverity.
          variants: [
            {
              props: { variant: 'standard', colorSeverity: 'success' },
              style: {
                backgroundColor: 'rgba(102, 187, 106, 0.15)',
                borderColor: 'rgba(102, 187, 106, 0.5)',
                color: '#81C784',
              },
            },
            {
              props: { variant: 'standard', colorSeverity: 'warning' },
              style: {
                backgroundColor: 'rgba(255, 167, 38, 0.15)',
                borderColor: 'rgba(255, 167, 38, 0.5)',
                color: '#FFB74D',
              },
            },
            {
              props: { variant: 'standard', colorSeverity: 'error' },
              style: {
                backgroundColor: 'rgba(239, 83, 80, 0.15)',
                borderColor: 'rgba(239, 83, 80, 0.5)',
                color: '#E57373',
              },
            },
            {
              props: { variant: 'standard', colorSeverity: 'info' },
              style: {
                backgroundColor: 'rgba(66, 165, 245, 0.15)',
                borderColor: 'rgba(66, 165, 245, 0.5)',
                color: '#64B5F6',
              },
            },
          ],
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#2D2D2D',
          borderRight: '1px solid rgba(255, 255, 255, 0.12)',
          borderRadius: 0,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(74, 159, 216, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(74, 159, 216, 0.3)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          // Ensure all typography has proper color inheritance
        },
        h1: {
          color: '#FFFFFF',
        },
        h2: {
          color: '#FFFFFF',
        },
        h3: {
          color: '#FFFFFF',
        },
        h4: {
          color: '#FFFFFF',
        },
        h5: {
          color: '#FFFFFF',
        },
        h6: {
          color: '#FFFFFF',
        },
        subtitle1: {
          color: '#FFFFFF',
        },
        subtitle2: {
          color: '#FFFFFF',
        },
        body1: {
          color: '#FFFFFF',
        },
        body2: {
          // Keep using text.secondary for body2 (it's meant for secondary text)
        },
        caption: {
          color: '#B0B0B0',  // Use secondary text color for captions
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: '#FFFFFF',
        },
        secondary: {
          color: '#B0B0B0',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#B0B0B0',
          '&.Mui-focused': {
            color: '#4A9FD8',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#B0B0B0',
          '&.Mui-focused': {
            color: '#4A9FD8',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: '#B0B0B0',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: '#B0B0B0',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(74, 159, 216, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(74, 159, 216, 0.3)',
            },
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#2D2D2D',
          border: '1px solid rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#2D2D2D',
          backgroundImage: 'none',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: '#FFFFFF',
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: '#B0B0B0',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#424242',
          color: '#FFFFFF',
          fontSize: '0.875rem',
          border: '1px solid rgba(255, 255, 255, 0.12)',
        },
        arrow: {
          color: '#424242',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#2D2D2D',
          border: '1px solid rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#B0B0B0',
          '&.Mui-selected': {
            color: '#4A9FD8',
          },
          '&:hover': {
            color: '#FFFFFF',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#4A9FD8',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          // Badge colors are already using theme.palette colors, so they'll use our updated semantic colors
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#B0B0B0',
          '&:hover': {
            color: '#FFFFFF',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#4A9FD8',
          '&:hover': {
            color: '#6AB5E5',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-track': {
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
          },
          '& .MuiSwitch-thumb': {
            backgroundColor: '#B0B0B0',
          },
          '& .Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#4A9FD8 !important',
          },
          '& .Mui-checked .MuiSwitch-thumb': {
            backgroundColor: '#FFFFFF',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#B0B0B0',
          '&.Mui-checked': {
            color: '#4A9FD8',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#B0B0B0',
          '&.Mui-checked': {
            color: '#4A9FD8',
          },
        },
      },
    },
  },
} as ThemeOptions);
// Create high contrast theme (WCAG AAA compliant)
export const highContrastTheme = createTheme({
  palette: {
    mode: 'light',
    contrastThreshold: 4.5, // WCAG AAA requires 7:1 for normal text, 4.5:1 for large text
    primary: {
      main: '#000080', // Navy blue for high contrast
      light: '#0000FF',
      dark: '#000033',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#800080', // Purple for high contrast
      light: '#9932CC',
      dark: '#4B0082',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#006400', // Dark green for high contrast
      light: '#228B22',
      dark: '#002800',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#B8860B', // Dark goldenrod for high contrast
      light: '#DAA520',
      dark: '#8B6914',
      contrastText: '#000000',
    },
    error: {
      main: '#8B0000', // Dark red for high contrast
      light: '#DC143C',
      dark: '#660000',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#191970', // Midnight blue for high contrast
      light: '#4169E1',
      dark: '#000033',
      contrastText: '#FFFFFF',
    },
    grey: colors.grey,
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#000000',
      secondary: '#333333',
    },
  },
  typography,
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600, // Increased font weight for better visibility
          borderRadius: 4,
          padding: '0.625rem 1.25rem', // Slightly larger padding
          border: '2px solid transparent', // For better boundary definition
          '&:focus': {
            outline: '3px solid #FFD700', // Gold outline for focus
            outlineOffset: '2px',
          },
        },
        sizeSmall: smallControlText,
        contained: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 4,
            '& fieldset': {
              borderWidth: '2px',
            },
            '&:hover fieldset': {
              borderWidth: '2px',
            },
            '&.Mui-focused fieldset': {
              borderWidth: '3px',
            },
          },
          '& .MuiInputLabel-root': {
            fontWeight: 600,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 0 0 2px #000000, 0 4px 8px rgba(0, 0, 0, 0.2)',
          '&:focus-within': {
            boxShadow: '0 0 0 3px #FFD700, 0 4px 8px rgba(0, 0, 0, 0.3)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '2px solid #000000',
        },
        elevation1: {
          boxShadow: '0 0 0 2px #000000, 0 2px 4px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          borderBottom: '2px solid #000000',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          borderRight: '2px solid #000000',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 600,
          border: '2px solid currentColor',
        },
        sizeSmall: smallChipText,
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 600,
          border: '2px solid currentColor',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          textDecorationThickness: '2px',
          '&:focus': {
            outline: '3px solid #FFD700',
            outlineOffset: '2px',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:focus': {
            outline: '3px solid #FFD700',
            outlineOffset: '2px',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          '&:focus': {
            outline: '3px solid #FFD700',
            outlineOffset: '2px',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-track': {
            border: '2px solid #000000',
            opacity: 1,
          },
          '& .MuiSwitch-thumb': {
            border: '2px solid #000000',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          },
          '&:focus-within .MuiSwitch-thumb': {
            outline: '3px solid #FFD700',
            outlineOffset: '2px',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&:focus': {
            outline: '3px solid #FFD700',
            outlineOffset: '2px',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&:focus': {
            outline: '3px solid #FFD700',
            outlineOffset: '2px',
          },
        },
      },
    },
  },
} as ThemeOptions);
